import { ImageSearch, Link as LinkIcon } from '@mui/icons-material';
import { Box, Button, TextField } from '@mui/material';
import React from 'react';
import FAQItemEditor from '../../../components/faq/FAQItemEditor';
import FAQItemView from '../../../components/faq/FAQItemView';
import { FAQData, FAQGroup, FAQItem } from '../../../components/faq/types';
import StyledSection from '../../../components/styles/StyledSection';
import { StyledHeading, StyledTypography } from '../../../components/styles/styles';
import FAQImageHelperModal from './FAQImageHelperModal';
import FAQLinkHelperModal from './FAQLinkHelperModal';

interface FAQAdminEditPanelProps {
  data: FAQData;
  selectedGroup: number;
  selectedItem: number;
  currentGroup?: FAQGroup;
  currentItem?: FAQItem;
  onUpdateItemQuestion: (groupIdx: number, itemIdx: number, value: string) => void;
  onUpdateItemAnswer: (groupIdx: number, itemIdx: number, value: string) => void;
  setInsertAnswer: (fn: ((html: string) => void) | null) => void;
  imageHelperOpen: boolean;
  setImageHelperOpen: (open: boolean) => void;
  imageHelperTab: 0 | 1;
  setImageHelperTab: (tab: 0 | 1) => void;
  assetAlt: string;
  setAssetAlt: (value: string) => void;
  publicAssetList: string[] | null;
  publicAssetFilter: string;
  setPublicAssetFilter: (value: string) => void;
  loadPublicAssetManifest: () => void;
  apiImages: { relativePath: string; contentType: string }[];
  loadApiImages: () => void;
  desiredFileName: string;
  setDesiredFileName: (value: string) => void;
  selectedUploadFile: File | null;
  setSelectedUploadFile: (file: File | null) => void;
  uploading: boolean;
  setUploading: (uploading: boolean) => void;
  handleUploadImage: (file: File) => Promise<void>;
  insertAnswer: ((html: string) => void) | null;
  faqLinkOpen: boolean;
  setFaqLinkOpen: (open: boolean) => void;
  faqLinkGroup: string;
  setFaqLinkGroup: (group: string) => void;
  faqLinkQuestion: string;
  setFaqLinkQuestion: (question: string) => void;
}

const FAQAdminEditPanel: React.FC<FAQAdminEditPanelProps> = ({
  data,
  selectedGroup,
  selectedItem,
  currentGroup,
  currentItem,
  onUpdateItemQuestion,
  onUpdateItemAnswer,
  setInsertAnswer,
  imageHelperOpen,
  setImageHelperOpen,
  imageHelperTab,
  setImageHelperTab,
  assetAlt,
  setAssetAlt,
  publicAssetList,
  publicAssetFilter,
  setPublicAssetFilter,
  loadPublicAssetManifest,
  apiImages,
  loadApiImages,
  desiredFileName,
  setDesiredFileName,
  selectedUploadFile,
  setSelectedUploadFile,
  uploading,
  setUploading,
  handleUploadImage,
  insertAnswer,
  faqLinkOpen,
  setFaqLinkOpen,
  faqLinkGroup,
  setFaqLinkGroup,
  faqLinkQuestion,
  setFaqLinkQuestion,
}) => (
  <Box sx={{ flex: 2, minWidth: 420 }}>
    {currentGroup && currentItem ? (
      <>
        <StyledSection tone="tone1">
          <StyledHeading headerVariant="subheading">Edit Question (plain text)</StyledHeading>
          <TextField
            value={currentItem.question}
            onChange={(e) => onUpdateItemQuestion(selectedGroup, selectedItem, e.target.value)}
            fullWidth
            placeholder="Enter question"
          />
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mt: 2 }}>
            <StyledHeading headerVariant="subheading">Edit Answer</StyledHeading>
            <Button
              size="small"
              startIcon={<ImageSearch />}
              onClick={() => setImageHelperOpen(true)}
            >
              Insert Image
            </Button>
            <Button
              size="small"
              startIcon={<LinkIcon />}
              onClick={() => setFaqLinkOpen(true)}
              sx={{ ml: 1 }}
            >
              Insert FAQ Link
            </Button>
          </Box>
          <FAQItemEditor
            key={`${selectedGroup}-${selectedItem}`}
            content={currentItem.answer}
            onSave={(html) => onUpdateItemAnswer(selectedGroup, selectedItem, html)}
            focusElement={true}
            setExternalInserter={(fn) => setInsertAnswer(() => fn)}
            onRevertToOriginal={() => {
              const originalItem = data.groups[selectedGroup].items[selectedItem];
              onUpdateItemAnswer(selectedGroup, selectedItem, originalItem.answer);
            }}
          />
        </StyledSection>
        <StyledSection tone="tone1" sx={{ mt: 2 }}>
          <StyledHeading headerVariant="subheading" sx={{ mt: 2 }}>
            Selected Item Preview
          </StyledHeading>
          <FAQItemView item={currentItem} expandable={false} />
        </StyledSection>
      </>
    ) : (
      <StyledTypography tone="default">Select a group and item to edit.</StyledTypography>
    )}

    <FAQImageHelperModal
      open={imageHelperOpen}
      onClose={() => setImageHelperOpen(false)}
      tab={imageHelperTab}
      onTabChange={setImageHelperTab}
      assetAlt={assetAlt}
      onAssetAltChange={setAssetAlt}
      publicAssetList={publicAssetList}
      publicAssetFilter={publicAssetFilter}
      onPublicAssetFilterChange={setPublicAssetFilter}
      onReloadPublicAssets={loadPublicAssetManifest}
      apiImages={apiImages}
      onReloadApiImages={loadApiImages}
      desiredFileName={desiredFileName}
      onDesiredFileNameChange={setDesiredFileName}
      selectedUploadFile={selectedUploadFile}
      onSelectUploadFile={setSelectedUploadFile}
      uploading={uploading}
      onUpload={async (file) => {
        setUploading(true);
        try {
          await handleUploadImage(file);
        } finally {
          setUploading(false);
        }
      }}
      onInsertPlaceholder={(placeholder) => insertAnswer?.(placeholder)}
    />

    <FAQLinkHelperModal
      open={faqLinkOpen}
      onClose={() => setFaqLinkOpen(false)}
      data={data}
      faqLinkGroup={faqLinkGroup}
      faqLinkQuestion={faqLinkQuestion}
      onGroupChange={setFaqLinkGroup}
      onQuestionChange={setFaqLinkQuestion}
      onInsertPlaceholder={(placeholder) => insertAnswer?.(placeholder)}
    />
  </Box>
);

export default FAQAdminEditPanel;
